import { useMemo } from 'react';
import { CapitalStructureBuildStructureDto } from '@app/shared/models/contracts/capital-structure-debt-instrument-dto';
import { findEarliestNextEventDate, findLatestPreviousEventDate } from '../editEventHelper';

interface UseEventDateBoundariesParams {
  eventId: string;
  buildStructure: CapitalStructureBuildStructureDto;
  investmentDate: string | undefined;
  yearEndDate: string | undefined;
  maxCapitalStructureYears: number;
}

interface DateBoundaries {
  picker: {
    minInclusiveDate: string | undefined;
    maxInclusiveDate: string | undefined;
  };
  validator: {
    minExclusiveDate: string | undefined;
    maxExclusiveDate: string | undefined;
  };
}

export const useEventDateBoundaries = ({
  eventId,
  buildStructure,
  investmentDate,
  yearEndDate,
  maxCapitalStructureYears,
}: UseEventDateBoundariesParams): DateBoundaries => {
  return useMemo(() => {
    const nextEventDate = findEarliestNextEventDate(eventId, buildStructure);
    const previousEventDate = findLatestPreviousEventDate(eventId, buildStructure);
    const minExclusiveDate = previousEventDate ?? investmentDate;

    const maxAllowedEventDate = yearEndDate
      ? new Date(yearEndDate).addYears(maxCapitalStructureYears).toISODateString()
      : undefined;

    // Picker boundaries are inclusive because calendar min/max are inclusive.
    // Validator boundaries are exclusive because dateBetweenValidator uses > and <.
    // We convert from the same canonical boundaries to keep picker and validation aligned.
    const maxExclusiveDate = nextEventDate
      ? nextEventDate
      : maxAllowedEventDate
      ? new Date(maxAllowedEventDate).addDays(1).toISODateString()
      : undefined;

    return {
      picker: {
        minInclusiveDate: minExclusiveDate
          ? new Date(minExclusiveDate).addDays(1).toISODateString()
          : undefined,
        maxInclusiveDate: nextEventDate
          ? new Date(nextEventDate).addDays(-1).toISODateString()
          : maxAllowedEventDate,
      },
      validator: {
        minExclusiveDate: minExclusiveDate,
        maxExclusiveDate: maxExclusiveDate,
      },
    };
  }, [eventId, buildStructure, investmentDate, yearEndDate, maxCapitalStructureYears]);
};
